"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.birthDateFormRules = exports.birthDateFieldMapping = exports.birthDateSchema = void 0;
exports.createBirthDateSchema = createBirthDateSchema;
exports.validateBirthDateData = validateBirthDateData;
exports.createBirthDateValidation = createBirthDateValidation;
const entities_1 = require("@common/entities");
// Error messages
const ERROR_MESSAGES = {
    REQUIRED: 'Birth date is required',
    INVALID_DATE: 'Date must be in MM/DD/YYYY format',
    FUTURE_DATES_NOT_ALLOWED: 'Future dates are not allowed.',
    AGE_TOO_YOUNG: (minAge) => `You must be at least ${minAge} years old to apply`,
    AGE_TOO_OLD: (maxAge) => `Must be less than ${maxAge} years old`,
};
/**
 * Common age calculation and validation logic
 */
function calculateAge(birthDate) {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    // Adjust if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}
function validateBirthDateAge(birthDate, minAge) {
    try {
        // Check for future dates
        const today = new Date();
        if (birthDate > today) {
            return { isValid: false, errorMessage: ERROR_MESSAGES.FUTURE_DATES_NOT_ALLOWED };
        }
        const age = calculateAge(birthDate);
        if (age < minAge) {
            return { isValid: false, errorMessage: ERROR_MESSAGES.AGE_TOO_YOUNG(minAge) };
        }
        return { isValid: true };
    }
    catch {
        return { isValid: false, errorMessage: ERROR_MESSAGES.INVALID_DATE };
    }
}
function validateBirthDateMaxAge(birthDate, maxAge) {
    try {
        const today = new Date();
        if (birthDate > today) {
            return { isValid: false, errorMessage: ERROR_MESSAGES.FUTURE_DATES_NOT_ALLOWED };
        }
        const age = calculateAge(birthDate);
        if (age >= maxAge) {
            return { isValid: false, errorMessage: ERROR_MESSAGES.AGE_TOO_OLD(maxAge) };
        }
        return { isValid: true };
    }
    catch {
        return { isValid: false, errorMessage: ERROR_MESSAGES.INVALID_DATE };
    }
}
/**
 * Creates a birth date validation schema with dynamic minimum age support
 */
function createBirthDateSchema(minAge = entities_1.FIELD_SETTINGS_DEFAULTS.DATE_OF_BIRTH.MIN_AGE) {
    return {
        fields: {
            applicant_birth_date: {
                required: true,
                dateFormat: {
                    format: 'MM/DD/YYYY',
                    message: ERROR_MESSAGES.INVALID_DATE,
                },
                custom: {
                    validate: (date) => {
                        const result = validateBirthDateAge(date, minAge);
                        return result.isValid;
                    },
                    message: ERROR_MESSAGES.AGE_TOO_YOUNG(minAge),
                },
            },
        },
        messages: {
            applicant_birth_date: {
                required: ERROR_MESSAGES.REQUIRED,
                dateFormat: ERROR_MESSAGES.INVALID_DATE,
                custom: ERROR_MESSAGES.AGE_TOO_YOUNG(minAge),
            },
        },
    };
}
/**
 * Default birth date validation schema (uses default minimum age from constants)
 */
exports.birthDateSchema = createBirthDateSchema();
/**
 * Field mapping for birth date fields
 */
exports.birthDateFieldMapping = {
    applicant_birth_date: 'applicant_birth_date',
};
/**
 * React Hook Form validation rules for birth date (with default minimum age from constants)
 */
exports.birthDateFormRules = {
    applicant_birth_date: {
        required: ERROR_MESSAGES.REQUIRED,
        validate: (value) => {
            // Check format first - handle both MM/DD/YYYY and YYYY-MM-DD formats
            if (typeof value === 'string' &&
                !/^\d{2}\/\d{2}\/\d{4}$/.test(value) &&
                !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return ERROR_MESSAGES.INVALID_DATE;
            }
            const date = new Date(value);
            const result = validateBirthDateAge(date, entities_1.FIELD_SETTINGS_DEFAULTS.DATE_OF_BIRTH.MIN_AGE);
            return result.isValid ? true : result.errorMessage;
        },
    },
};
/**
 * Validates birth date data with dynamic minimum age
 */
function validateBirthDateData(data) {
    const errors = {};
    let isValid = true;
    const { applicant_birth_date, minAge } = data;
    // Required validation
    if (!applicant_birth_date) {
        errors.applicant_birth_date = [ERROR_MESSAGES.REQUIRED];
        isValid = false;
        return { isValid, errors };
    }
    // Date format validation - handle both MM/DD/YYYY and YYYY-MM-DD formats
    const mmddyyyyRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    const yyyymmddRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!mmddyyyyRegex.test(applicant_birth_date) && !yyyymmddRegex.test(applicant_birth_date)) {
        errors.applicant_birth_date = [ERROR_MESSAGES.INVALID_DATE];
        isValid = false;
        return { isValid, errors };
    }
    // Date validity check
    const date = new Date(applicant_birth_date);
    const isValidDate = date instanceof Date && !isNaN(date.getTime());
    if (!isValidDate) {
        errors.applicant_birth_date = [ERROR_MESSAGES.INVALID_DATE];
        isValid = false;
        return { isValid, errors };
    }
    // Age validation
    const minimumAge = minAge ?? entities_1.FIELD_SETTINGS_DEFAULTS.DATE_OF_BIRTH.MIN_AGE;
    const result = validateBirthDateAge(date, minimumAge);
    if (!result.isValid) {
        errors.applicant_birth_date = [result.errorMessage || ERROR_MESSAGES.INVALID_DATE];
        isValid = false;
    }
    return { isValid, errors };
}
/**
 * Creates a validation function with optional minimum and/or maximum age
 * @param minAge - Person must be at least this old (e.g. 18 for adults)
 * @param maxAge - Person must be younger than this (e.g. 17 for minors)
 */
function createBirthDateValidation(minAge, maxAge) {
    return {
        applicant_birth_date: {
            required: ERROR_MESSAGES.REQUIRED,
            validate: (value) => {
                // Check format first - handle both MM/DD/YYYY and YYYY-MM-DD formats
                if (typeof value === 'string' &&
                    !/^\d{2}\/\d{2}\/\d{4}$/.test(value) &&
                    !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    return ERROR_MESSAGES.INVALID_DATE;
                }
                const date = new Date(value);
                if (minAge !== undefined) {
                    const result = validateBirthDateAge(date, minAge);
                    if (!result.isValid)
                        return result.errorMessage;
                }
                if (maxAge !== undefined) {
                    const result = validateBirthDateMaxAge(date, maxAge);
                    if (!result.isValid)
                        return result.errorMessage;
                }
                return true;
            },
        },
    };
}
